import type { AxiosResponse } from "axios";

import type {
  MultiLegOrderType,
  MultiLegTimeInForce,
} from "~/v2/constants/multiLegOrder";
import type { PositionIntent } from "~/v2/constants/optionOrder";
import instance from "..";
import { looseParse } from "../looseParse";
import type { Order } from "./account";
import { orderSchema } from "./account.schema";

interface CreateOrderRequestBase {
  symbol: string;
  type: "market" | "limit" | "stop" | "stop_limit" | "trailing_stop";
  time_in_force: "day" | "gtc" | "fok" | "ioc" | "opg" | "cls";
  qty?: string;
  notional?: string;
  limit_price?: string;
  stop_price?: string;
  trail_price?: string;
  trail_percent?: string;
  extended_hours?: boolean;
  client_order_id?: string;
  order_class?: "simple" | "bracket" | "oco" | "oto";
  take_profit?: Record<string, string>;
  stop_loss?: Record<string, string>;
}

export interface CreateOrderWithSide extends CreateOrderRequestBase {
  side: "buy" | "sell";
  position_intent?: never;
}

export interface CreateOrderWithIntent extends CreateOrderRequestBase {
  side?: never;
  position_intent:
    | "buy_to_open"
    | "buy_to_close"
    | "sell_to_open"
    | "sell_to_close";
}

export type CreateOrderRequest = CreateOrderWithSide | CreateOrderWithIntent;

export const createOrder = async (
  accountId: string,
  order: CreateOrderRequest,
  product: "paper" | "live",
) => {
  const accountType = product === "live" ? "accounts" : "paper_accounts";

  const res = await instance.post<CreateOrderRequest, AxiosResponse<Order>>(
    `/${accountType}/${accountId}/orders`,
    order,
  );
  res.data = looseParse(orderSchema, res.data, "createOrder");
  return res;
};

export interface CreateMultiLegOrderRequest {
  type: MultiLegOrderType;
  time_in_force: MultiLegTimeInForce;
  limit_price?: string;
  qty?: string;
  legs: {
    symbol: string;
    side: "buy" | "sell";
    ratio_qty: string;
    position_intent?: PositionIntent;
  }[];
}

export const createMultiLegOrder = async (
  accountId: string,
  order: CreateMultiLegOrderRequest,
  product: "paper" | "live",
) => {
  const accountType = product === "live" ? "accounts" : "paper_accounts";

  const res = await instance.post<
    CreateMultiLegOrderRequest,
    AxiosResponse<Order>
  >(`/${accountType}/${accountId}/orders`, {
    ...order,
    order_class: "mleg",
  });
  res.data = looseParse(orderSchema, res.data, "createMultiLegOrder");
  return res;
};
